import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, Check, MapPin, Loader2 } from "lucide-react";

// Leaflet's default marker icon paths break under Webpack — we have to inline
// the icon URLs from the CDN. (Standard react-leaflet gotcha.)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Same reverse-geocoder used by lib/location.js, kept inline so this component
// can be imported standalone. Returns a marine-friendly short name or null.
async function _reverseGeocode(lat, lng) {
    try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=12&addressdetails=1`;
        const res = await fetch(url, { headers: { "Accept-Language": navigator.language || "en" } });
        if (!res.ok) return null;
        const data = await res.json();
        const a = data.address || {};
        const parts = [
            a.bay || a.harbour || a.water || a.marina || a.suburb,
            a.village || a.town || a.city || a.hamlet,
            a.state_district || a.state || a.region || a.county,
            a.country_code ? a.country_code.toUpperCase() : a.country,
        ].filter(Boolean);
        const deduped = parts.filter((p, i) => i === 0 || p !== parts[i - 1]);
        if (deduped.length > 0) return deduped.slice(0, 3).join(", ");
        if (data.display_name) return data.display_name.split(",").slice(0, 3).join(",").trim();
    } catch { /* silent — caller will keep raw coords */ }
    return null;
}

// Internal child that listens for map clicks → updates the pin. Must be a
// proper React function component (uppercase) because useMapEvents is a hook.
function ClickHandler({ onPick }) {
    useMapEvents({
        click(e) {
            onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });
    return null;
}

// Force Leaflet to recompute its container size after mount. Without this
// the tile layer can render blank when the map is instantiated while its
// parent modal is animating in — Leaflet caches the initial (possibly zero)
// container size and never re-measures unless nudged.
function MapReady() {
    const map = useMap();
    useEffect(() => {
        // A single rAF tick is enough for the modal transition to settle;
        // a follow-up timeout catches iOS Safari's slower composite.
        const raf = requestAnimationFrame(() => map.invalidateSize());
        const to = setTimeout(() => map.invalidateSize(), 300);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(to);
        };
    }, [map]);
    return null;
}

/**
 * LocationPickerMap — modal map that lets the captain correct a bad GPS fix.
 *
 * Common case: laptop on Starlink showing the wrong ground-station location,
 * captain wants to drag/tap the pin to where they actually are.
 *
 * Props:
 *   initial: { lat, lng, place_name? } — current position to centre on
 *   onCancel: () => void
 *   onConfirm: ({ lat, lng, place_name }) => void
 */
export default function LocationPickerMap({ initial, onCancel, onConfirm }) {
    const [pin, setPin] = useState(() => ({
        lat: Number(initial?.lat) || 0,
        lng: Number(initial?.lng) || 0,
    }));
    const [placeName, setPlaceName] = useState(initial?.place_name || "");
    const [geoBusy, setGeoBusy] = useState(false);
    const debounceRef = useRef(null);

    // Debounced reverse-geocode on pin change — captains will drag the pin a
    // few times before settling, no need to hammer Nominatim.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setGeoBusy(true);
            const name = await _reverseGeocode(pin.lat, pin.lng);
            setGeoBusy(false);
            if (name) setPlaceName(name);
        }, 600);
        return () => clearTimeout(debounceRef.current);
    }, [pin.lat, pin.lng]);

    const handleConfirm = () => {
        onConfirm({
            lat: pin.lat,
            lng: pin.lng,
            place_name: placeName || `${pin.lat.toFixed(4)}°, ${pin.lng.toFixed(4)}°`,
        });
    };

    // Initial map center — captured ONCE on mount. We deliberately don't
    // re-center as the user drags the pin (the map would chase the marker
    // and feel jumpy). ESLint can't tell this is intentional, hence the
    // disable directive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const center = useMemo(() => [pin.lat, pin.lng], []);

    // Drag handler for the marker — keep both lat/lng updates atomic
    const dragHandlers = useMemo(() => ({
        dragend(e) {
            const ll = e.target.getLatLng();
            setPin({ lat: ll.lat, lng: ll.lng });
        },
    }), []);

    // Lock body scroll while the picker is open. Prevents the parent modal
    // (EntryModal / CruiserContactModal) from being scrollable underneath the
    // overlay and keeps the map centered even on iOS Safari.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    return createPortal(
        <div
            data-testid="location-picker-modal"
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed", inset: 0, zIndex: 9999,
                background: "rgba(4, 12, 24, 0.85)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 16,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div
                style={{
                    background: "var(--bm-bg)", borderRadius: 12,
                    border: "1px solid var(--bm-border-soft)",
                    width: "min(720px, 100%)",
                    maxHeight: "92vh", display: "flex", flexDirection: "column",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                }}
            >
                {/* Header */}
                <div style={{
                    padding: "14px 18px", borderBottom: "1px solid var(--bm-border-soft)",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <MapPin size={18} color="var(--bm-gold)" />
                        <h3 className="bm-serif" style={{ fontSize: 17, margin: 0, fontWeight: 500 }}>
                            Adjust your location
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        data-testid="location-picker-close"
                        className="bm-btn bm-btn-ghost"
                        style={{ padding: "4px 8px" }}
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Helper copy */}
                <div style={{ padding: "10px 18px", fontSize: 12.5, color: "var(--bm-text-muted)" }}>
                    Drag the pin — or tap anywhere on the map — to where you actually are. Useful when
                    Starlink / your laptop&apos;s IP gives you a ground-station location instead of the boat.
                </div>

                {/* Map */}
                <div style={{ flex: 1, minHeight: 360, position: "relative" }}>
                    <MapContainer
                        center={center}
                        zoom={9}
                        style={{ height: "100%", minHeight: 360, width: "100%" }}
                        scrollWheelZoom={true}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />
                        <Marker
                            position={[pin.lat, pin.lng]}
                            draggable={true}
                            eventHandlers={dragHandlers}
                        />
                        <ClickHandler onPick={setPin} />
                        <MapReady />
                    </MapContainer>
                </div>

                {/* Footer — place name + coords + buttons */}
                <div style={{
                    padding: "12px 18px", borderTop: "1px solid var(--bm-border-soft)",
                    display: "flex", flexDirection: "column", gap: 10,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: "var(--bm-text-dim)" }}>Location:</span>
                        <span data-testid="location-picker-place" style={{ fontSize: 13, color: "var(--bm-text)", fontWeight: 500 }}>
                            {geoBusy ? <Loader2 size={12} className="bm-spin" /> : (placeName || "—")}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--bm-text-dim)" }}>
                            ({pin.lat.toFixed(4)}°, {pin.lng.toFixed(4)}°)
                        </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            data-testid="location-picker-cancel"
                            className="bm-btn bm-btn-ghost"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            data-testid="location-picker-confirm"
                            className="bm-btn bm-btn-primary"
                        >
                            <Check size={13} /> Use this location
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
